import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MdClear, MdComment, MdFileDownload, MdSearch, MdWarningAmber } from "react-icons/md";
import { format, parseISO, startOfDay, endOfDay } from "date-fns";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import CommentModal from "../components/common/CommentModal";
import MultiUserSelect from "../components/common/MultiUserSelect";

const exportToCsv = (filename, headers, rows) => {
  const encode = (val) => {
    if (val === null || val === undefined) return "";
    const str = String(val).replace(/"/g, '""');
    return `"${str}"`;
  };
  const allRows = [headers, ...rows];
  const csv = allRows.map((r) => r.map(encode).join(",")).join("\r\n");
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const toIdString = (value) => {
  if (!value) return "";
  return typeof value === "object" ? String(value._id || value.id || "") : String(value);
};

const taskHasAnyAssignee = (task, selectedUserIds) => {
  if (!Array.isArray(selectedUserIds) || selectedUserIds.length === 0) return true;
  const targets = new Set(selectedUserIds.map(String));
  const assignedToId = toIdString(task?.assignedTo);
  if (assignedToId && targets.has(assignedToId)) return true;
  const assignedUsers = Array.isArray(task?.assignedUsers) ? task.assignedUsers : [];
  return assignedUsers.some((u) => targets.has(toIdString(u)));
};

const matchesAnyUserId = (userRef, selectedUserIds) => {
  if (!Array.isArray(selectedUserIds) || selectedUserIds.length === 0) return true;
  const id = toIdString(userRef);
  if (!id) return false;
  return selectedUserIds.map(String).includes(id);
};

const formatSelectedUserNames = (users, selectedIds, fallback = "All") => {
  if (!Array.isArray(selectedIds) || selectedIds.length === 0) return fallback;
  const names = users
    .filter((u) => selectedIds.map(String).includes(String(u._id)))
    .map((u) => u.name)
    .filter(Boolean);
  if (names.length === 0) return fallback;
  if (names.length <= 3) return names.join(", ");
  return `${names.slice(0, 3).join(", ")} +${names.length - 3} more`;
};

/** Assignees other than the creator (matches Manage Staff Tasks). */
const displayAssignedUsersForTask = (task) => {
  const creatorId = toIdString(task?.createdBy);
  const multi =
    Array.isArray(task?.assignedUsers) && task.assignedUsers.length
      ? task.assignedUsers
      : task?.assignedTo
        ? [task.assignedTo]
        : [];
  return multi.filter((assignedUser) => toIdString(assignedUser) !== creatorId);
};

const formatAssignedToLabel = (task) => {
  const displayAssigned = displayAssignedUsersForTask(task);
  if (displayAssigned.length === 0) return "Self created task";
  return displayAssigned.map((u) => u?.name || "User").join(", ");
};

const NotDoneTasksReport = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentTask, setCommentTask] = useState(null);

  const [search, setSearch] = useState("");
  const [assignedToIds, setAssignedToIds] = useState([]);
  const [createdByIds, setCreatedByIds] = useState([]);
  const [coAssignedUserIds, setCoAssignedUserIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [hasCommentsFilter, setHasCommentsFilter] = useState("");
  const [deadlineFrom, setDeadlineFrom] = useState("");
  const [deadlineTo, setDeadlineTo] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  useEffect(() => {
    const load = async () => {
      try {
        const [tRes, uRes] = await Promise.all([
          api.get("/tasks/admin/all", { params: { reportsView: 1 } }),
          api.get("/users/list"),
        ]);
        setTasks(tRes.data.data || []);
        setUsers(uRes.data.data || []);
      } finally {
        setLoading(false);
      }
    };
    if (isAdmin) load();
  }, [isAdmin]);

  const allUsers = useMemo(
    () => users.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [users],
  );

  const filteredTasks = useMemo(() => {
    return tasks
      .filter((task) => (task.status || "") !== "done")
      .filter((task) => taskHasAnyAssignee(task, assignedToIds))
      .filter((task) => matchesAnyUserId(task.createdBy, createdByIds))
      .filter((task) => matchesAnyUserId(task.coAssignedTo, coAssignedUserIds))
      .filter((task) => {
        if (!statusFilter) return true;
        return (task.status || "") === statusFilter;
      })
      .filter((task) => {
        if (!priorityFilter) return true;
        return (task.priority || "") === priorityFilter;
      })
      .filter((task) => {
        if (!hasCommentsFilter) return true;
        const hasComments = Array.isArray(task.comments) && task.comments.length > 0;
        return hasCommentsFilter === "yes" ? hasComments : !hasComments;
      })
      .filter((task) => {
        if (!deadlineFrom && !deadlineTo) return true;
        if (!task.deadline) return false;
        const dueDate = new Date(task.deadline);
        if (deadlineFrom && dueDate < startOfDay(parseISO(deadlineFrom))) return false;
        if (deadlineTo && dueDate > endOfDay(parseISO(deadlineTo))) return false;
        return true;
      })
      .filter((task) => {
        if (!createdFrom && !createdTo) return true;
        if (!task.createdAt) return false;
        const createdDate = new Date(task.createdAt);
        if (createdFrom && createdDate < startOfDay(parseISO(createdFrom))) return false;
        if (createdTo && createdDate > endOfDay(parseISO(createdTo))) return false;
        return true;
      })
      .filter((task) => {
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        const text = [
          task.title || "",
          task.description || "",
          task.assignedTo?.name || "",
          ...(Array.isArray(task.assignedUsers)
            ? task.assignedUsers.map((u) => u?.name || "")
            : []),
          task.coAssignedTo?.name || "",
          task.createdBy?.name || "",
          task.status || "",
          task.priority || "",
          ...(task.tags || []),
        ]
          .join(" ")
          .toLowerCase();
        return text.includes(q);
      })
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [
    tasks,
    search,
    assignedToIds,
    createdByIds,
    coAssignedUserIds,
    statusFilter,
    priorityFilter,
    hasCommentsFilter,
    deadlineFrom,
    deadlineTo,
    createdFrom,
    createdTo,
  ]);

  const clearAllFilters = () => {
    setSearch("");
    setAssignedToIds([]);
    setCreatedByIds([]);
    setCoAssignedUserIds([]);
    setStatusFilter("");
    setPriorityFilter("");
    setHasCommentsFilter("");
    setDeadlineFrom("");
    setDeadlineTo("");
    setCreatedFrom("");
    setCreatedTo("");
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [
    search,
    assignedToIds,
    createdByIds,
    coAssignedUserIds,
    statusFilter,
    priorityFilter,
    hasCommentsFilter,
    deadlineFrom,
    deadlineTo,
    createdFrom,
    createdTo,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredTasks.length / PAGE_SIZE));
  const pageStartIndex = (page - 1) * PAGE_SIZE;
  const currentPageTasks = filteredTasks.slice(
    pageStartIndex,
    pageStartIndex + PAGE_SIZE,
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const reportRows = useMemo(
    () =>
      filteredTasks.map((t) => [
        t.title || "Untitled task",
        t.createdBy?.name || "N/A",
        formatAssignedToLabel(t),
        t.coAssignedTo?.name || "No co assignee",
        t.status || "N/A",
        t.priority || "N/A",
        t.deadline ? format(new Date(t.deadline), "MMM d, yyyy") : "N/A",
      ]),
    [filteredTasks],
  );

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const subtitle = [
      `Assigned To: ${formatSelectedUserNames(allUsers, assignedToIds)}`,
      `Created By: ${formatSelectedUserNames(allUsers, createdByIds)}`,
      `Co-Assignee: ${formatSelectedUserNames(allUsers, coAssignedUserIds)}`,
      `Status: ${statusFilter || "All open statuses"}`,
      `Priority: ${priorityFilter || "All"}`,
      `Generated: ${format(new Date(), "MMM d, yyyy hh:mm a")}`,
    ].join(" | ");
    doc.setFontSize(14);
    doc.text("Not Done Tasks Report", 14, 14);
    doc.setFontSize(9);
    doc.text(subtitle, 14, 21);
    autoTable(doc, {
      startY: 26,
      head: [[
        "Task",
        "Created By",
        "Assigned To",
        "Co-Assignee",
        "Status",
        "Priority",
        "Due Date",
      ]],
      body: reportRows,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [99, 102, 241] },
    });
    doc.save("not-done-tasks-report.pdf");
  };

  if (!isAdmin) {
    return (
      <main className="page">
        <div className="card card-body">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <MdWarningAmber size={26} color="var(--primary)" />
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Not done tasks report
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "var(--text-muted)",
                  marginTop: 4,
                }}
              >
                This report is available for authorized report roles only.
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="page">
        <div className="loading">
          <div className="spinner" />
          <span className="loading-text">Loading not done tasks report...</span>
        </div>
      </main>
    );
  }

  return (
    <main className="page">
      {commentTask && (
        <CommentModal
          taskId={commentTask._id}
          taskTitle={commentTask.title}
          onClose={() => setCommentTask(null)}
        />
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">
            <MdWarningAmber style={{ marginRight: 6 }} />
            Not Done Tasks Report
          </h1>
          <p className="page-subtitle">
            Track all incomplete tasks with advanced filters for assigned staff, co-assignee,
            status, priority, and date ranges.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() =>
              exportToCsv(
                "not-done-tasks-report.csv",
                [
                  "Task",
                  "Created By",
                  "Assigned To",
                  "Co-Assignee",
                  "Status",
                  "Priority",
                  "Due Date",
                ],
                reportRows,
              )
            }
          >
            <MdFileDownload size={15} style={{ marginRight: 4 }} />
            Export Excel
          </button>
          <button type="button" className="btn btn-secondary" onClick={exportPdf}>
            <MdFileDownload size={15} style={{ marginRight: 4 }} />
            Export PDF
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div
          className="card-body"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            alignItems: "flex-end",
            justifyContent: "flex-start",
          }}
        >
          <div style={{ minWidth: 280, flex: 1 }}>
            <label className="form-label">Search</label>
            <div style={{ position: "relative" }}>
              <MdSearch
                size={16}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-muted)",
                }}
              />
              <input
                className="form-control"
                style={{ paddingLeft: 34 }}
                placeholder="Search task, assignee, creator, status, priority..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div style={{ minWidth: 240 }}>
            <label className="form-label">Assigned To</label>
            <MultiUserSelect
              users={allUsers}
              value={assignedToIds}
              onChange={setAssignedToIds}
              placeholder="All staff"
            />
          </div>
          <div style={{ minWidth: 240 }}>
            <label className="form-label">Created By</label>
            <MultiUserSelect
              users={allUsers}
              value={createdByIds}
              onChange={setCreatedByIds}
              placeholder="All creators"
            />
          </div>
          <div style={{ minWidth: 240 }}>
            <label className="form-label">Co-Assignee</label>
            <MultiUserSelect
              users={allUsers}
              value={coAssignedUserIds}
              onChange={setCoAssignedUserIds}
              placeholder="All co-assignees"
            />
          </div>
          <div style={{ minWidth: 170 }}>
            <label className="form-label">Status</label>
            <select
              className="form-control"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All open statuses</option>
              <option value="todo">To Do</option>
              <option value="in-progress">In Progress</option>
              <option value="review">Review</option>
            </select>
          </div>
          <div style={{ minWidth: 170 }}>
            <label className="form-label">Priority</label>
            <select
              className="form-control"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="">All priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div style={{ minWidth: 170 }}>
            <label className="form-label">Comments</label>
            <select
              className="form-control"
              value={hasCommentsFilter}
              onChange={(e) => setHasCommentsFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="yes">Has comments</option>
              <option value="no">No comments</option>
            </select>
          </div>
          <div style={{ minWidth: 180 }}>
            <label className="form-label">Due Date From</label>
            <input
              className="form-control"
              type="date"
              value={deadlineFrom}
              onChange={(e) => setDeadlineFrom(e.target.value)}
            />
          </div>
          <div style={{ minWidth: 180 }}>
            <label className="form-label">Due Date To</label>
            <input
              className="form-control"
              type="date"
              value={deadlineTo}
              onChange={(e) => setDeadlineTo(e.target.value)}
            />
          </div>
          <div style={{ minWidth: 180 }}>
            <label className="form-label">Created From</label>
            <input
              className="form-control"
              type="date"
              value={createdFrom}
              onChange={(e) => setCreatedFrom(e.target.value)}
            />
          </div>
          <div style={{ minWidth: 180 }}>
            <label className="form-label">Created To</label>
            <input
              className="form-control"
              type="date"
              value={createdTo}
              onChange={(e) => setCreatedTo(e.target.value)}
            />
          </div>
          <div className="stat-card" style={{ minWidth: 170 }}>
            <div
              style={{
                fontSize: "0.7rem",
                textTransform: "uppercase",
                color: "var(--text-muted)",
                marginBottom: 4,
              }}
            >
              Matching not done tasks
            </div>
            <div className="stat-value">{filteredTasks.length}</div>
          </div>
          <button type="button" className="btn btn-secondary" onClick={clearAllFilters}>
            <MdClear size={15} style={{ marginRight: 4 }} />
            Clear Filters
          </button>
        </div>
      </div>

      <div className="card">
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--border)",
            fontWeight: 700,
            fontSize: "0.9rem",
          }}
        >
          Not done tasks list
        </div>
        {filteredTasks.length === 0 ? (
          <div className="empty-state">
            <MdWarningAmber size={40} />
            <h3>No not-done tasks found</h3>
            <p>Try changing filters and date ranges.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Created By</th>
                  <th>Assigned To</th>
                  <th>Co-Assignee</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Due Date</th>
                  <th style={{ textAlign: "center", width: 80 }}>Comments</th>
                </tr>
              </thead>
              <tbody>
                {currentPageTasks.map((t) => (
                  <tr key={t._id}>
                    <td style={{ fontWeight: 600 }}>
                      <Link
                        to={`/tasks/${t._id}`}
                        state={{ returnTo: `${location.pathname}${location.search}` }}
                        style={{ color: "var(--primary)", textDecoration: "none" }}
                      >
                        {t.title || "Untitled task"}
                      </Link>
                    </td>
                    <td className="text-muted">{t.createdBy?.name || "N/A"}</td>
                    <td>
                      {(() => {
                        const displayAssigned = displayAssignedUsersForTask(t);
                        if (displayAssigned.length === 0) {
                          return (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "4px 10px",
                                borderRadius: 999,
                                background: "var(--primary-light)",
                                color: "var(--primary)",
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                textTransform: "capitalize",
                                whiteSpace: "nowrap",
                              }}
                            >
                              self created task
                            </span>
                          );
                        }
                        return (
                          <span className="text-muted">
                            {displayAssigned.map((u) => u?.name || "User").join(", ")}
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      {t.coAssignedTo?.name ? (
                        <span className="text-muted">{t.coAssignedTo.name}</span>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "4px 10px",
                            borderRadius: 999,
                            background: "#f1f5f9",
                            color: "#94a3b8",
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            border: "1px dashed #cbd5e1",
                            textTransform: "capitalize",
                          }}
                        >
                          no co assignee
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${t.status || "todo"}`}>{t.status || "N/A"}</span>
                    </td>
                    <td>
                      <span className={`badge badge-${t.priority || "medium"}`}>
                        {t.priority || "N/A"}
                      </span>
                    </td>
                    <td className="text-muted">
                      {t.deadline ? format(new Date(t.deadline), "MMM d, yyyy") : "N/A"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        type="button"
                        className="btn-icon"
                        title="View comments"
                        onClick={() => setCommentTask(t)}
                        style={{ position: "relative" }}
                      >
                        <MdComment size={18} />
                        {Array.isArray(t.comments) && t.comments.length > 0 && (
                          <span
                            style={{
                              position: "absolute",
                              top: 1,
                              right: 1,
                              width: 8,
                              height: 8,
                              background: "#ef4444",
                              borderRadius: "50%",
                              border: "1px solid #fff",
                            }}
                          />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {filteredTasks.length > 0 && (
        <div
          style={{
            marginTop: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            fontSize: "0.8rem",
          }}
        >
          <span style={{ color: "var(--text-muted)" }}>
            Showing{" "}
            <strong>
              {pageStartIndex + 1}-{Math.min(pageStartIndex + PAGE_SIZE, filteredTasks.length)}
            </strong>{" "}
            of <strong>{filteredTasks.length}</strong> filtered tasks
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              className="btn btn-sm btn-secondary"
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                className="btn btn-sm"
                onClick={() => setPage(p)}
                style={{
                  minWidth: 32,
                  paddingInline: 10,
                  fontWeight: 600,
                  background: p === page ? "var(--primary)" : "var(--surface2)",
                  color: p === page ? "#fff" : "var(--text)",
                  borderColor: p === page ? "var(--primary)" : "var(--border)",
                }}
              >
                {p}
              </button>
            ))}
            <button
              className="btn btn-sm btn-secondary"
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => (p < totalPages ? p + 1 : totalPages))}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default NotDoneTasksReport;
